import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { isUndef } from '@/lib/utils';

export interface DistrictState {
    _provinceList: District[];
    /** 额外一级列表 */
    extraProvinceList: District[];
    _listMap: {
        [districtId: number]: District[];
    };
    _districtMap: {
        [districtId: number]: District;
    };
}

/**
 * 默认的id为-1，表明当前没有处理该数据，没有数据为0，表明在该上级行政区下，没有对应的下级行政区
 *
 * 默认从服务器返回的数据结构为 {id, name}
 */
const m: Module<DistrictState, RootState> = {
    state: () => ({
        // 所有的province列表原始数据
        _provinceList: [],
        extraProvinceList: [
            {
                id: -1,
                name: '全国',
                level: 1,
                is_city: false,
            },
        ],
        // 所有的 id -> 子类的list
        _listMap: {},
        // 所有的district内容的map，即id -> district<{id, level, name}>
        _districtMap: {
            '-1': {
                id: -1,
                name: '全国',
                level: 1,
                is_city: false,
            },
        },
    }),
    mutations: {
        /**
         * 准备存储data
         *
         * @param {Number} id 父地区id
         * @param {Array} list 某次请求获得的list内容
         */
        addList(state, { id, list }) {
            Vue.set(state._listMap, id, list);
            // 保存到map中
            list.forEach((district: District) => {
                Vue.set(state._districtMap, String(district.id), district);
            });
        },
        /**
         * 设置provinceList
         *
         * @param {Array} provinceList 所有的province列表 [{id: 1, name: '北京市'}]
         */
        setProvinceList(state, provinceList) {
            state._provinceList = provinceList;
        },
    },
    actions: {
        /**
         * 获取省列表
         *
         * @param {} forceUpdate 强制更新
         *
         * @return {Array} ProvinceList
         */
        async getProvinceList({ state, commit }, { forceUpdate } = {}) {
            if (state._provinceList.length === 0) {
                const response = await Vue.axios.get('/api/v1/framework/top_level_districts');
                const rdata = response.data;
                if (rdata.status === 0) {
                    const data = rdata.data as District[];
                    commit('setProvinceList', data);
                    commit('addList', {
                        id: 0, // 对于provinceList来说，id是默认为0的
                        list: data,
                    });
                }
            }
            return state._provinceList;
        },

        /**
         * 获取列表
         *
         * @param {} forceUpdate 强制更新
         * @param {} id 父级地区id
         *
         * @return {} 子行政区的内容
         */
        async getChildDistricts({ state, commit }, { forceUpdate, id } = {}) {
            if (isUndef(state._listMap[id]) || forceUpdate) {
                const response = await Vue.axios.get('/api/v1/framework/child_districts', {
                    params: {
                        id,
                    },
                });
                const rdata = response.data;
                if (rdata.status === 0) {
                    commit('addList', {
                        id,
                        list: rdata.data,
                    });
                }
            }
            return state._listMap[id];
        },
    },
};

export default m;
